import { useQueryClient } from '@tanstack/react-query';
import { ArrowUp, Square } from 'lucide-react';
import {
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useModelChoice } from '../hooks/useModelChoice';
import { audioMessageText } from '../lib/audioRecording';
import { useAuth } from '../lib/auth';
import {
  addDraftFiles,
  getChatDraft,
  getDraftEpoch,
  updateChatDraft,
  uploadDraftAttachments,
  useChatDraft,
} from '../lib/chatDrafts';
import { fileKeys, uploadWorkspaceFiles } from '../lib/files';
import { type Effort, type Model, type PermissionMode, readPermission } from '../lib/settings';
import { AttachmentPill } from './AttachmentPill';
import { AudioRecorder } from './AudioRecorder';
import { PlusMenu } from './PlusMenu';
import { SkillsDrawer } from './SkillsDrawer';
import {
  type SlashCommand,
  SlashCommandsPopover,
  filterCommands,
  slashQueryForText,
} from './SlashCommandsPopover';

type Props = {
  draftScope: string;
  placeholder?: string;
  disabled?: boolean;
  streaming?: boolean;
  onSubmit?: (
    text: string,
    model: Model,
    permission: PermissionMode,
    attachments: string[],
    effort: Effort,
  ) => void | Promise<void>;
  onAbort?: () => void;
  /**
   * Files dropped onto the page-level zone — parent passes them in
   * so dropzone state lives once per ChatPage, not per Composer.
   */
  externalFiles?: File[];
  onExternalFilesConsumed?: () => void;
  /**
   * One-shot model the picker should latch onto — set when the server
   * reports a safety-refusal swap (Fable → Opus) so subsequent turns
   * continue on the fallback model instead of re-tripping the filter.
   * Consumed via `onModelSwitchConsumed` right after it's applied.
   */
  modelSwitch?: Model | null;
  onModelSwitchConsumed?: () => void;
  /**
   * One-shot draft loaded into the textarea (edit sent user message).
   * Parent clears via `onDraftConsumed` after apply.
   */
  draftText?: string | null;
  onDraftConsumed?: () => void;
  /** Extra toolbar control after the plus menu («Мак» access level). */
  toolbarExtra?: ReactNode;
};

/**
 * Chat composer. Enter submits; Shift+Enter inserts a newline.
 * Attachments live in local state until the submit fires — at
 * which point their server paths are handed over with the text.
 * Upload starts eagerly on selection / drop so by the time the
 * user finishes typing, paths are usually ready.
 */
export function Composer({
  draftScope,
  placeholder = 'Спросите что-нибудь',
  disabled,
  streaming,
  onSubmit,
  onAbort,
  externalFiles,
  onExternalFilesConsumed,
  modelSwitch,
  onModelSwitchConsumed,
  draftText,
  onDraftConsumed,
  toolbarExtra,
}: Props) {
  const { user } = useAuth();
  const profileId = user?.id ?? null;
  const qc = useQueryClient();
  const allowedModels = user?.allowed_models ?? null;
  const noModels = allowedModels !== null && allowedModels.length === 0;
  const draft = useChatDraft(draftScope);
  const { text, attachments } = draft;
  const { model, effort, setModel } = useModelChoice();
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [slashIndex, setSlashIndex] = useState(0);
  const [dismissedSlash, setDismissedSlash] = useState<{ scope: string; version: number } | null>(
    null,
  );
  const [uploadError, setUploadError] = useState<{ scope: string; message: string } | null>(null);
  const [audioBusy, setAudioBusy] = useState(false);
  const audioBusyRef = useRef(false);
  const onAudioBusyChange = useCallback((busy: boolean) => {
    audioBusyRef.current = busy;
    setAudioBusy(busy);
  }, []);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const submittingScopesRef = useRef(new Set<string>());
  const currentScopeRef = useRef(draftScope);
  currentScopeRef.current = draftScope;
  const recordingEpoch = getDraftEpoch();
  const recordingScope = `${draftScope}\0${recordingEpoch}\0${draft.editTarget?.messageId ?? ''}`;
  const currentRecordingScope = useRef(recordingScope);
  currentRecordingScope.current = recordingScope;

  function setText(next: string | ((previous: string) => string)): void {
    updateChatDraft(draftScope, (current) => ({
      ...current,
      text: typeof next === 'function' ? next(current.text) : next,
    }));
  }

  const slashQuery = slashQueryForText(text);
  const slashMatches = useMemo(
    () => (slashQuery === null ? [] : filterCommands(slashQuery)),
    [slashQuery],
  );
  const slashActive =
    slashQuery !== null &&
    slashMatches.length > 0 &&
    !(dismissedSlash?.scope === draftScope && dismissedSlash.version === draft.textVersion);
  const slashPickIndex = slashActive ? Math.min(slashIndex, slashMatches.length - 1) : 0;

  // Filter change invalidates a previous arrow-key index. The popover
  // already clamps the highlight; Enter must use the same index.
  // biome-ignore lint/correctness/useExhaustiveDependencies: slashQuery is the reset trigger
  useEffect(() => {
    setSlashIndex(0);
  }, [slashQuery]);

  // Drain files the parent dropzone gave us. Must live in an
  // effect — invoking state updates from the render body double-
  // fires in StrictMode and also violates React's rules.
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot drain of dropped files
  useEffect(() => {
    if (!externalFiles || externalFiles.length === 0) return;
    void startUpload(externalFiles);
    onExternalFilesConsumed?.();
  }, [externalFiles]);

  // Load edited user-message text into the composer.
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot draft load
  useEffect(() => {
    if (draftText == null || draftText === '') return;
    setText(draftText);
    onDraftConsumed?.();
    requestAnimationFrame(() => {
      const ta = taRef.current;
      if (!ta) return;
      ta.focus();
      ta.selectionStart = ta.selectionEnd = ta.value.length;
      autoGrow(ta);
    });
  }, [draftText]);

  // Latch the picker onto a server-driven safety-refusal swap so the
  // *next* turn continues on the fallback model. Clamp to the user's
  // allowlist defensively — if the fallback isn't permitted the clamp
  // keeps a valid selection rather than leaking a forbidden one. Same
  // one-shot drain pattern as externalFiles above.
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot model-switch drain
  useEffect(() => {
    if (!modelSwitch) return;
    setModel(modelSwitch); // clamped to the allowlist inside
    onModelSwitchConsumed?.();
  }, [modelSwitch]);

  async function startUpload(files: File[], source: 'file' | 'microphone' = 'file'): Promise<void> {
    const scope = draftScope;
    const epoch = recordingEpoch;
    if (!user || currentScopeRef.current !== scope || epoch !== getDraftEpoch()) return;
    const reservedSlots = audioBusyRef.current && source !== 'microphone' ? 1 : 0;
    const pending = addDraftFiles(scope, files, epoch, {
      reservedSlots,
      keepLocalFiles: source === 'microphone',
    });
    if (pending.length === 0) {
      setUploadError({
        scope,
        message: reservedSlots
          ? 'Одно из 10 мест для вложений занято текущей записью. Удалите другой файл.'
          : 'К сообщению можно прикрепить не больше 10 файлов.',
      });
      return;
    }
    setUploadError(null);
    if (await uploadDraftAttachments(scope, pending, files, epoch, uploadWorkspaceFiles)) {
      void qc.invalidateQueries({ queryKey: fileKeys.user(user.id) });
    }
  }

  async function retryAttachment(id: string): Promise<void> {
    const scope = draftScope;
    const epoch = recordingEpoch;
    if (!user || currentScopeRef.current !== scope || epoch !== getDraftEpoch()) return;
    const attachment = getChatDraft(scope).attachments.find((item) => item.id === id);
    if (!attachment?.localFile || attachment.uploading) return;
    if (
      await uploadDraftAttachments(
        scope,
        [attachment],
        [attachment.localFile],
        epoch,
        uploadWorkspaceFiles,
      )
    ) {
      void qc.invalidateQueries({ queryKey: fileKeys.user(user.id) });
    }
  }

  function onPick(e: ChangeEvent<HTMLInputElement>): void {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (files.length > 0) void startUpload(files);
  }

  function onPaste(e: ClipboardEvent<HTMLTextAreaElement>): void {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pasted: File[] = [];
    for (const item of Array.from(items)) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const f = item.getAsFile();
        if (f) {
          // Screenshots come through as "image.png"; keep it explicit
          // so the filename isn't literally empty on disk.
          const ext = f.type.split('/')[1] ?? 'png';
          const named = new File([f], f.name || `pasted-${Date.now()}.${ext}`, {
            type: f.type,
          });
          pasted.push(named);
        }
      }
    }
    if (pasted.length > 0) {
      e.preventDefault();
      void startUpload(pasted);
    }
  }

  function removeAttachment(id: string): void {
    updateChatDraft(draftScope, (current) => ({
      ...current,
      attachments: current.attachments.filter((a) => a.id !== id),
    }));
    setUploadError((current) => (current?.scope === draftScope ? null : current));
  }

  const paths = attachments.flatMap((attachment) => (attachment.path ? [attachment.path] : []));
  const submitText = audioMessageText(text, paths);
  const canSubmit =
    !disabled &&
    !noModels &&
    !streaming &&
    !audioBusy &&
    submitText.length > 0 &&
    attachments.length <= 10 &&
    attachments.every((a) => !a.uploading && !a.error);

  async function submit(): Promise<void> {
    if (!canSubmit || audioBusyRef.current) return;
    const scope = draftScope;
    if (submittingScopesRef.current.has(scope)) return;
    submittingScopesRef.current.add(scope);
    const epoch = getDraftEpoch();
    const sentTextVersion = draft.textVersion;
    const sentAttachmentIds = new Set(attachments.map((a) => a.id));
    const value = submitText;
    try {
      await onSubmit?.(value, model, readPermission(), paths, effort);
    } catch {
      // Keep the draft. Caller (or mutation.error) surfaces the reason.
      return;
    } finally {
      submittingScopesRef.current.delete(scope);
    }
    updateChatDraft(
      scope,
      (current) => ({
        ...current,
        text:
          current.textVersion === sentTextVersion && current.text.trim() === text.trim()
            ? ''
            : current.text,
        attachments: current.attachments.filter((a) => !sentAttachmentIds.has(a.id)),
      }),
      epoch,
    );
    setUploadError((current) => (current?.scope === scope ? null : current));
    if (currentScopeRef.current === scope && taRef.current) taRef.current.style.height = 'auto';
  }

  function pickSlash(cmd: SlashCommand): void {
    setText(cmd.prompt);
    setSlashIndex(0);
    queueMicrotask(() => taRef.current?.focus());
  }

  function pickSkill(skill: string): void {
    setSkillsOpen(false);
    const insert = `Используй навык **${skill}**, чтобы `;
    setText((prev) => (prev.trim() ? `${insert}${prev}` : insert));
    queueMicrotask(() => taRef.current?.focus());
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (slashActive) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashIndex((i) => Math.min(slashMatches.length - 1, i + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashIndex((i) => Math.max(0, i - 1));
        return;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        const c = slashMatches[slashPickIndex];
        if (c) pickSlash(c);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
    }
  }

  function autoGrow(el: HTMLTextAreaElement): void {
    // `window.innerHeight` on iOS stays at the full-screen value
    // even with the keyboard up; visualViewport shrinks to the real
    // visible area so capping by it keeps the textarea from growing
    // under the keyboard.
    const vh = window.visualViewport?.height ?? window.innerHeight;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, vh * 0.4)}px`;
  }

  return (
    <div className="mc-composer mc-float relative rounded-3xl border border-border/70 bg-surface">
      <SkillsDrawer open={skillsOpen} onClose={() => setSkillsOpen(false)} onPick={pickSkill} />
      {slashActive && slashQuery !== null && (
        <SlashCommandsPopover
          query={slashQuery}
          activeIndex={slashPickIndex}
          onPick={pickSlash}
          onMove={(delta) =>
            setSlashIndex((i) => Math.max(0, Math.min(slashMatches.length - 1, i + delta)))
          }
          onClose={() => setDismissedSlash({ scope: draftScope, version: draft.textVersion })}
        />
      )}
      {uploadError?.scope === draftScope && (
        <div role="alert" className="mx-4 mt-3 text-[13px] text-destructive">
          {uploadError.message}
        </div>
      )}
      {draft.recovery && (
        <div
          role="alert"
          className="mx-4 mt-3 rounded-lg border border-warning/40 bg-subtle px-3 py-2 text-[12px]"
        >
          <div>
            Подтверждение не получено. Проверьте историю перед повтором. Сообщение сохранено для
            восстановления.
          </div>
          <div className="mt-1 line-clamp-2 text-muted-foreground">{draft.recovery.text}</div>
          <button
            type="button"
            disabled={audioBusy}
            className="mt-2 min-h-9 rounded-md border border-border px-2.5 text-foreground hover:bg-bubble"
            onClick={() => {
              if (audioBusyRef.current) return;
              if (
                (text || attachments.length > 0) &&
                !window.confirm('Заменить текущий черновик неотправленным сообщением?')
              )
                return;
              updateChatDraft(draftScope, (current) =>
                current.recovery
                  ? {
                      ...current,
                      text: current.recovery.text,
                      attachments: current.recovery.attachments,
                      editTarget: null,
                      recovery: null,
                    }
                  : current,
              );
            }}
          >
            Восстановить сообщение
          </button>
        </div>
      )}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 px-5 pt-3">
          {attachments.map((a) => (
            <AttachmentPill
              key={a.id}
              attachment={a}
              onRemove={() => removeAttachment(a.id)}
              onRetry={() => void retryAttachment(a.id)}
            />
          ))}
        </div>
      )}
      <div className="px-4 pt-3.5 pb-1">
        <textarea
          ref={taRef}
          rows={1}
          value={text}
          placeholder={
            noModels ? 'Нет доступных моделей — обратитесь к администратору' : placeholder
          }
          aria-label="Сообщение"
          disabled={disabled || noModels}
          onChange={(e) => {
            setText(e.target.value);
            autoGrow(e.target);
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          className="w-full resize-none bg-transparent text-[16px] outline-none placeholder:text-[oklch(var(--placeholder))] disabled:cursor-not-allowed"
        />
      </div>
      <input ref={fileRef} type="file" multiple hidden onChange={onPick} tabIndex={-1} />
      <div className="relative flex flex-nowrap items-center gap-1 px-2 pb-2">
        {!audioBusy && (
          <PlusMenu
            disabled={disabled || streaming}
            onAttach={() => fileRef.current?.click()}
            onOpenSkills={() => setSkillsOpen(true)}
          />
        )}
        {!audioBusy && toolbarExtra}
        {!audioBusy && <div className="flex-1" />}
        {(audioBusy || !streaming) && (
          <AudioRecorder
            key={recordingScope}
            disabled={
              disabled || streaming || noModels || profileId === null || attachments.length >= 10
            }
            isCurrent={() =>
              currentRecordingScope.current === recordingScope && getDraftEpoch() === recordingEpoch
            }
            onBusyChange={onAudioBusyChange}
            onError={(message) => setUploadError(message ? { scope: draftScope, message } : null)}
            onFile={(file) => void startUpload([file], 'microphone')}
          />
        )}
        {streaming ? (
          <button
            type="button"
            onClick={onAbort}
            className="mc-press mc-pop flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-foreground text-background"
            title="Остановить"
            aria-label="Остановить"
          >
            <Square size={13} fill="currentColor" />
          </button>
        ) : submitText.length > 0 && !audioBusy ? (
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!canSubmit}
            className="mc-press mc-pop flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-40"
            title="Отправить"
            aria-label="Отправить"
          >
            <ArrowUp size={20} strokeWidth={2.2} />
          </button>
        ) : null}
      </div>
    </div>
  );
}
